import { useEffect, useState, useMemo, useCallback } from "react";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { CaretLeft, CaretRight, MapPin, Clock, Car, User, Calendar as CalIcon } from "@phosphor-icons/react";
import { toast } from "sonner";
import { addMonths, endOfMonth, sameDay, dateOnly, buildGrid } from "../lib/calendarUtils";

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const fmtDateTime = (s) => (s ? new Date(s).toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");
const fmtTime = (s) => (s ? new Date(s).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" }) : "");
const statusBadge = (s) => {
  if (s === "completed") return <Badge className="bg-emerald-600 hover:bg-emerald-600">Completed</Badge>;
  if (s === "current") return <Badge className="bg-blue-600 hover:bg-blue-600">Confirmed</Badge>;
  if (s === "pre_booking") return <Badge variant="secondary">Upcoming</Badge>;
  return <Badge variant="outline">{s || "—"}</Badge>;
};

export default function MySchedule() {
  const [bookings, setBookings] = useState([]);
  const [driverName, setDriverName] = useState("");
  const [hasDriver, setHasDriver] = useState(true);
  const [loading, setLoading] = useState(true);
  const [cursor, setCursor] = useState(() => new Date());
  const [selectedDay, setSelectedDay] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/my-schedule");
      setBookings(data.bookings || []);
      setDriverName(data.driver_name || "");
      setHasDriver(!!data.chauffeur_id);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const grid = useMemo(() => buildGrid(cursor), [cursor]);
  const monthLabel = cursor.toLocaleDateString(undefined, { month: "long", year: "numeric" });

  const eventsByDay = useMemo(() => {
    const map = {};
    for (const b of bookings) {
      const d = dateOnly(b.start_datetime);
      if (!d) continue;
      const key = d.toDateString();
      (map[key] = map[key] || []).push(b);
    }
    return map;
  }, [bookings]);

  const upcoming = useMemo(
    () => [...bookings].filter((b) => b.start_datetime).sort((a, b) => new Date(a.start_datetime) - new Date(b.start_datetime)),
    [bookings]
  );
  const selectedEvents = selectedDay ? (eventsByDay[selectedDay.toDateString()] || []) : [];

  return (
    <div className="space-y-6" data-testid="my-schedule-page">
      <div>
        <div className="label-mono">Driver</div>
        <h2 className="font-heading text-3xl mt-1">My Schedule{driverName ? ` — ${driverName}` : ""}</h2>
        <p className="text-sm text-muted-foreground mt-1">Your assigned chauffeur trips and tours. View-only.</p>
      </div>

      {!hasDriver ? (
        <div className="border border-amber-700/50 bg-amber-950/10 p-8 text-center text-muted-foreground" data-testid="my-schedule-nodriver">
          Your login isn’t linked to a driver yet. Ask an admin to tie your account to a driver in Settings → Team access.
        </div>
      ) : (
        <Tabs defaultValue="list">
          <TabsList data-testid="my-schedule-tabs">
            <TabsTrigger value="list" data-testid="my-schedule-tab-list">List</TabsTrigger>
            <TabsTrigger value="calendar" data-testid="my-schedule-tab-calendar">Calendar</TabsTrigger>
          </TabsList>

          {/* LIST */}
          <TabsContent value="list" className="mt-6">
            {loading ? (
              <div className="text-muted-foreground">Loading…</div>
            ) : upcoming.length === 0 ? (
              <div className="border border-border bg-card p-12 text-center text-muted-foreground" data-testid="my-schedule-empty">
                No trips assigned to you yet.
              </div>
            ) : (
              <div className="space-y-3" data-testid="my-schedule-list">
                {upcoming.map((b) => (
                  <div key={b.id} className="border border-border bg-card p-5" data-testid={`schedule-card-${b.id}`}>
                    <div className="flex items-start justify-between flex-wrap gap-3">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="capitalize">{b.booking_type === "tour" ? "Tour" : "Chauffeur"}</Badge>
                        {statusBadge(b.status)}
                      </div>
                      <div className="text-sm font-heading flex items-center gap-1.5"><Clock size={14} className="text-primary" /> {fmtDateTime(b.start_datetime)}</div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2 mt-4 text-sm">
                      {b.customer_name && <Row icon={User} label="Client" value={b.customer_name + (b.customer_phone ? ` · ${b.customer_phone}` : "")} />}
                      {(b.service_type_detail || b.tour_name) && <Row icon={CalIcon} label="Service" value={b.tour_name || b.service_type_detail} />}
                      {b.vehicle_label && <Row icon={Car} label="Vehicle" value={b.vehicle_label + (b.vehicle_plate ? ` · ${b.vehicle_plate}` : "")} />}
                      {b.duration && <Row icon={Clock} label="Duration" value={b.duration} />}
                      {b.start_location && <Row icon={MapPin} label="Pickup" value={b.start_location} />}
                      {b.end_location && <Row icon={MapPin} label="Drop-off" value={b.end_location} />}
                      {b.in_between_stops && <Row icon={MapPin} label="Stops" value={b.in_between_stops} />}
                      {b.passenger_count > 0 && <Row icon={User} label="Passengers" value={String(b.passenger_count)} />}
                    </div>
                    {b.notes && <div className="mt-3 text-xs text-muted-foreground border-t border-border/40 pt-3">{b.notes}</div>}
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* CALENDAR */}
          <TabsContent value="calendar" className="mt-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-xl" data-testid="my-schedule-month">{monthLabel}</h3>
              <div className="flex gap-2">
                <Button variant="outline" size="icon" onClick={() => { setCursor(addMonths(cursor, -1)); setSelectedDay(null); }} data-testid="cal-prev"><CaretLeft size={16} /></Button>
                <Button variant="outline" size="sm" onClick={() => { setCursor(new Date()); setSelectedDay(null); }} data-testid="cal-today">Today</Button>
                <Button variant="outline" size="icon" onClick={() => { setCursor(addMonths(cursor, 1)); setSelectedDay(null); }} data-testid="cal-next"><CaretRight size={16} /></Button>
              </div>
            </div>
            <div className="grid grid-cols-7 border-l border-t border-border" data-testid="my-schedule-calendar">
              {WEEKDAYS.map((d) => (
                <div key={d} className="border-r border-b border-border bg-card px-2 py-2 label-mono text-xs">{d}</div>
              ))}
              {grid.map((day, i) => {
                const evs = eventsByDay[day.toDateString()] || [];
                const inMonth = day.getMonth() === cursor.getMonth();
                const isToday = sameDay(day, new Date());
                return (
                  <button
                    type="button"
                    key={i}
                    onClick={() => setSelectedDay(day)}
                    className={`min-h-[90px] border-r border-b border-border p-1.5 text-left align-top transition-colors hover:bg-accent/40 ${inMonth ? "bg-background" : "bg-card/40 text-muted-foreground"} ${selectedDay && sameDay(day, selectedDay) ? "ring-1 ring-primary" : ""}`}
                    data-testid={`cal-day-${day.toISOString().slice(0, 10)}`}
                  >
                    <div className={`text-xs ${isToday ? "text-primary font-heading" : ""}`}>{day.getDate()}</div>
                    <div className="mt-1 space-y-1">
                      {evs.slice(0, 3).map((b) => (
                        <div key={b.id} className="text-[10px] leading-tight px-1 py-0.5 rounded bg-primary/15 text-primary truncate">
                          {fmtTime(b.start_datetime)} {b.customer_name || (b.booking_type === "tour" ? "Tour" : "Trip")}
                        </div>
                      ))}
                      {evs.length > 3 && <div className="text-[10px] text-muted-foreground">+{evs.length - 3} more</div>}
                    </div>
                  </button>
                );
              })}
            </div>

            {selectedDay && (
              <div className="mt-5 border border-border bg-card p-5" data-testid="cal-day-detail">
                <h4 className="font-heading text-lg mb-3">{selectedDay.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}</h4>
                {selectedEvents.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No trips this day.</p>
                ) : (
                  <div className="space-y-3">
                    {selectedEvents.map((b) => (
                      <div key={b.id} className="border-b border-border/40 pb-3 last:border-0 last:pb-0" data-testid={`cal-event-${b.id}`}>
                        <div className="flex items-center gap-2 text-sm font-heading">
                          <Clock size={14} className="text-primary" /> {fmtTime(b.start_datetime)}{b.end_datetime ? ` – ${fmtTime(b.end_datetime)}` : ""}
                          <span className="ml-1">{statusBadge(b.status)}</span>
                        </div>
                        <div className="text-sm text-muted-foreground mt-1.5 space-y-0.5">
                          {b.customer_name && <div>Client: {b.customer_name}{b.customer_phone ? ` · ${b.customer_phone}` : ""}</div>}
                          {(b.start_location || b.end_location) && <div>{[b.start_location, b.end_location].filter(Boolean).join(" → ")}</div>}
                          {b.vehicle_label && <div>{b.vehicle_label}</div>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}

function Row({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-2">
      <Icon size={15} className="text-muted-foreground mt-0.5 shrink-0" />
      <div>
        <span className="label-mono text-xs">{label}: </span>
        <span>{value}</span>
      </div>
    </div>
  );
}
